/**
 * Auth0 Organization invitation acceptance.
 *
 * Auth0 never links invitees straight to Universal Login. The invitation email
 * points at the invite application's **Application Login URI** with the ticket
 * attached:
 *
 *   https://studio.rapidsos.com/?invitation=<ticket>&organization=org_…&organization_name=…
 *
 * The app owns the next hop: it has to forward `invitation` and `organization`
 * to `/authorize`. Drop them and the ticket is never redeemed — the SDK starts
 * an ordinary login against the build-time `VITE_AUTH0_ORGANIZATION`, which is
 * not necessarily the org the person was invited to.
 */

export interface InvitationParams {
  /** Auth0 invitation ticket id. */
  invitation: string;
  /** Auth0 organization id (`org_…`) the ticket belongs to. */
  organization: string;
  /** Org display name Auth0 puts in the link; presentation only. */
  organizationName?: string;
}

const INVITATION_PARAM_KEYS = ['invitation', 'organization', 'organization_name'] as const;

function trimmed(value: string | null): string | undefined {
  const next = value?.trim();
  return next ? next : undefined;
}

/**
 * Invitation parameters from a query string, or null when this is an ordinary
 * page load. Both `invitation` and `organization` are required: a ticket
 * without its org cannot be redeemed, and Auth0 always sends the pair.
 */
export function readInvitationParams(search: string): InvitationParams | null {
  const params = new URLSearchParams(search);
  const invitation = trimmed(params.get('invitation'));
  const organization = trimmed(params.get('organization'));
  if (!invitation || !organization) return null;

  const organizationName = trimmed(params.get('organization_name'));
  return {
    invitation,
    organization,
    ...(organizationName ? { organizationName } : {}),
  };
}

/**
 * The same location with the invitation parameters removed, so a redeemed (or
 * abandoned) ticket does not sit in the address bar and get replayed on reload.
 */
export function withoutInvitationParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of INVITATION_PARAM_KEYS) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
